import { useState, useCallback, useEffect } from 'react';
import axiosInstance from 'api/axiosInstance';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

export interface CommentAttachment {
	Id: number;
	FileName: string;
	Description: string;
	FileSize: number;
	UploadedBy: string;
	UploadedOn: string;
	Url: string;
}

interface UseCommentAttachmentsParams {
	commentId: number;
	caseId: number;
	customerId: number;
}

function useCommentAttachments({ commentId, caseId, customerId }: UseCommentAttachmentsParams) {
	const [attachments, setAttachments] = useState<CommentAttachment[]>([]);
	const [loading, setLoading] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<CommentAttachment | null>(null);
	const { notifySuccess, notifyError } = useNotificationProvider();

	const fetchAttachments = useCallback(async () => {
		if (!commentId) return;
		setLoading(true);
		try {
			const params = `CommentId=${commentId}&CaseId=${caseId}&CustomerId=${customerId}`;
			const response = await axiosInstance.get(
				`/api/fileuploadservice/GetAttachments?${params}`,
			);
			const data = response?.data;
			// Normalize response — fileuploadservice may use Name/Size vs FileName/FileSize
			const normalized: CommentAttachment[] = (Array.isArray(data) ? data : []).map(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(f: any) => ({
					Id: f.Id,
					FileName: f.FileName || f.Name || '',
					Description: f.Description || '',
					FileSize: Number(f.FileSize || f.Size || 0),
					UploadedBy: f.UploadedBy || f.CreatedBy || '',
					UploadedOn: f.UploadedOn || f.CreatedOn || '',
					Url: f.Url || `/api/fileuploadservice/Download?FileId=${f.Id}&CommentId=${commentId}&CaseId=${caseId}&CustomerId=${customerId}`,
				}),
			);
			setAttachments(normalized);
		} catch (err) {
			console.error('[CommentAttachments] Error fetching:', err);
		} finally {
			setLoading(false);
		}
	}, [commentId, caseId, customerId]);

	useEffect(() => {
		fetchAttachments();
	}, [fetchAttachments]);

	const deleteAttachment = useCallback(async (attachmentId: number) => {
		try {
			await axiosInstance.post(
				`/api/fileuploadservice/Delete?FileId=${attachmentId}`,
				{ fileId: attachmentId },
				{ headers: { 'Content-Type': 'application/json;charset=UTF-8' } },
			);
			notifySuccess('Attachment deleted');
			await fetchAttachments();
		} catch (err) {
			console.error('[CommentAttachments] Error deleting:', err);
			notifyError('Failed to delete attachment');
		}
	}, [fetchAttachments, notifySuccess, notifyError]);

	const openUploadModal = useCallback(() => setUploadModalOpen(true), []);
	const closeUploadModal = useCallback(() => setUploadModalOpen(false), []);
	const onUploadSuccess = useCallback(() => { fetchAttachments(); }, [fetchAttachments]);

	const openDeleteDialog = useCallback((a: CommentAttachment) => setDeleteTarget(a), []);
	const closeDeleteDialog = useCallback(() => setDeleteTarget(null), []);
	const confirmDelete = useCallback(async () => {
		if (deleteTarget) {
			await deleteAttachment(deleteTarget.Id);
			setDeleteTarget(null);
		}
	}, [deleteTarget, deleteAttachment]);

	return {
		state: { attachments, loading, uploadModalOpen, deleteTarget },
		handler: {
			fetchAttachments,
			openUploadModal,
			closeUploadModal,
			onUploadSuccess,
			openDeleteDialog,
			closeDeleteDialog,
			confirmDelete,
		},
	};
}

export default useCommentAttachments;
